import { inject, Injectable } from '@angular/core';
import { ICorrelationCurve } from '@app/api/interfaces/chart-data/correlation-curve';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import {
  ChartDatasetConfig,
  ChartDataService,
  ExtendedChartDataset,
} from '@app/core/services/charts/chart-data.service';
import { ChartData, ChartOptions, TooltipItem, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { splitTextByLines } from '@app/shared/utils/split-label-by-words';
import { roundToTwoDecimals } from '@app/shared/utils/math';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { FloatingPopupLineChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-line-chart.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { Length } from '@app/shared/units/length/length.model';
import { MetricLengthUnit } from '@app/shared/units/length/length-unit.enum';
import { measurementSystemLengthUnit_m_ft } from '@app/core/localize/measurement-system-length-unit';
import { ILocalizationKey } from '@app/core/interfaces/localization';

interface IFpCorrelationGraphChartTooltipConfig {
  measurementSystem: MeasurementSystem;
  middleXAxis: number;
}

@Injectable()
export class FpCorrelationGraphChartService {
  protected floatingPopupLineChartService = inject(FloatingPopupLineChartService);
  protected chartDataService = inject(ChartDataService);
  protected translate: TranslateService = inject(TranslateService);
  protected colorManagementService = inject(ColorManagementService);
  protected localizeMeasurementSystem = {
    [MeasurementSystem.Metric]: _(`@@shared-measurement-system-metric__m`),
    [MeasurementSystem.Imperial]: _(`@@shared-measurement-system-imperial__ft`),
  };

  private noDataLocalizationKey: ILocalizationKey = _(`@@shared-no-data-value-placeholder`);

  public generateOptions(data: ICorrelationCurve[], measurementSystem: MeasurementSystem): ChartOptions {
    const xValues = data.map(el => this.dataConverter(el, measurementSystem).x);
    const maxXAxis = Math.max(...xValues);
    const minXAxis = Math.min(...xValues);
    const calculatedMiddle = (maxXAxis + minXAxis) / 2;
    const middleXAxis = xValues.reduce((prev, curr) => {
      return Math.abs(curr - calculatedMiddle) < Math.abs(prev - calculatedMiddle) ? curr : prev;
    });
    const xLabel = this.localizeMeasurementSystem[measurementSystem];

    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions({
      measurementSystem,
      middleXAxis,
    });

    return this.floatingPopupLineChartService.generateOptions(
      {
        yLabel: null,
        xLabel,
        titleText: null,
      },
      minXAxis,
      maxXAxis,
      true,
      tooltipOptions
    );
  }

  public generateMinimizedOptions(data: ICorrelationCurve[], measurementSystem: MeasurementSystem): ChartOptions {
    const xValues = data.map(el => this.dataConverter(el, measurementSystem).x);
    const maxXAxis = Math.max(...xValues);
    const minXAxis = Math.min(...xValues);

    const xLabel = this.localizeMeasurementSystem[measurementSystem];

    const calculatedMiddle = (maxXAxis + minXAxis) / 2;

    const middleXAxis = xValues.reduce((prev, curr) => {
      return Math.abs(curr - calculatedMiddle) < Math.abs(prev - calculatedMiddle) ? curr : prev;
    });

    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions({
      measurementSystem,
      middleXAxis,
    });

    return this.floatingPopupLineChartService.generateOptions(
      {
        yLabel: null,
        xLabel,
        titleText: null,
      },
      minXAxis,
      maxXAxis,
      true,
      tooltipOptions,
      false
    );
  }

  private dataConverter(data: ICorrelationCurve, viewMeasurementSystem: MeasurementSystem): { x: number; y: number } {
    const rawX = data.position
      ? new Length(data.position, MetricLengthUnit.Meter).round(
          measurementSystemLengthUnit_m_ft[viewMeasurementSystem],
          2
        )
      : null;

    return {
      y: roundToTwoDecimals(data.normalizedValue) || 0,
      x: roundToTwoDecimals(rawX) || 0,
    };
  }

  private generateDatasetConfig(
    viewMeasurementSystem: MeasurementSystem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interactionsEnabled: boolean
  ): ChartDatasetConfig<ICorrelationCurve>[] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getMainColor = (item: ICorrelationCurve, index: number) => {
      return this.colorManagementService.getChartDefaultColor();
    };

    return [
      {
        label: '',
        tooltipLabel: '',
        type: 'line',
        dataValue: (data: ICorrelationCurve) => this.dataConverter(data, viewMeasurementSystem),
        borderColor: (item, index) => getMainColor(item, index).toRgbString(), // --p-blue-500
        backgroundColor: (item, index) => {
          const color = getMainColor(item, index);

          return this.colorManagementService.getColorInstanceWithOpacity(color, 0.4).toRgbString();
        },
        borderWidth: 1,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 2,
        pointBackgroundColor: (item, index) => getMainColor(item, index).toRgbString(),
        pointBorderColor: (item, index) => getMainColor(item, index).toRgbString(),
        pointBorderWidth: 1,
      },
    ];
  }

  public getDataForChart(data: ICorrelationCurve[], viewMeasurementSystem: MeasurementSystem): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICorrelationCurve>[] = this.generateDatasetConfig(
      viewMeasurementSystem,
      true
    );

    return {
      datasets: this.chartDataService.generateDatasets<ICorrelationCurve>([...data], datasetConfigs),
    };
  }
  public getMinimizedDataForChart(data: ICorrelationCurve[], viewMeasurementSystem: MeasurementSystem): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICorrelationCurve>[] = this.generateDatasetConfig(
      viewMeasurementSystem,
      false
    );

    return {
      datasets: this.chartDataService.generateDatasets<ICorrelationCurve>([...data], datasetConfigs),
    };
  }

  private getTooltipOptions(config: IFpCorrelationGraphChartTooltipConfig): _DeepPartialObject<TooltipOptions> {
    const localizeTooltipXYValue = {
      [MeasurementSystem.Metric]: _(`@@chart-xy-x__m`),
      [MeasurementSystem.Imperial]: _(`@@chart-xy-x__ft`),
    };

    const localizeTooltipCenterDistance = {
      [MeasurementSystem.Metric]: _(`@@chart-center-distance__m`),
      [MeasurementSystem.Imperial]: _(`@@chart-center-distance__ft`),
    };

    return {
      callbacks: {
        title: () => {
          const text = this.translate.instant(_('@@shared-cross-correlation'));

          return splitTextByLines(text, 40);
        },
        afterBody: (tooltipItems: TooltipItem<'line'>[]) => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);

          const tooltipItem = tooltipItems[0];

          const currentX = tooltipItems[0].parsed.x;

          const distanceToTheCenterRaw = currentX - config.middleXAxis;
          const toTheCenter = this.floatingPopupLineChartService.formatChartNumber(distanceToTheCenterRaw, 0, 2);
          const dataset: ExtendedChartDataset<ICorrelationCurve> = tooltipItem.dataset;
          const data = dataset.meta ? dataset.meta[tooltipItem.dataIndex] : null;

          const centerDistanceInfo = this.translate.instant(localizeTooltipCenterDistance[config.measurementSystem], {
            I18N_VAR_DISTANCE: toTheCenter,
          });
          const dtValue = data ? this.floatingPopupLineChartService.formatChartNumber(data.time, 0, 3) : noDataText;
          const dTInfo = this.translate.instant(_(`@@chart-delta-time`), {
            I18N_VAR_TIME: dtValue,
          });

          return [centerDistanceInfo, dTInfo];
        },
        label: tooltipItem => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);

          const { x, y } = tooltipItem.raw as { x: number; y: number };

          const xFormattedValue = this.floatingPopupLineChartService.formatChartNumber(x, 0, 2);
          const yFormattedValue = this.floatingPopupLineChartService.formatChartNumber(y, 0, 2);

          const separator = '   ';

          return this.translate.instant(localizeTooltipXYValue[config.measurementSystem], {
            I18N_VAR_X_VALUE: xFormattedValue ?? noDataText,
            I18N_VAR_SEPARATOR: separator,
            I18N_VAR_Y_VALUE: yFormattedValue ?? noDataText,
          });
        },
      },
    };
  }
}
